import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  Layout, 
  Menu, 
  Button, 
  Space, 
  Card, 
  Typography, 
  Tag, 
  Radio, 
  Checkbox, 
  message, 
  Modal, 
  Spin, 
  Row, 
  Col,
  Tooltip,
  Progress,
  Badge,
  Affix,
  Divider,
  Anchor,
  Image
} from 'antd';
import {
  SaveOutlined,
  CheckOutlined,
  ClockCircleOutlined,
  SoundOutlined,
  CheckCircleOutlined,
  QuestionCircleOutlined,
  CodeOutlined,
  CheckSquareOutlined,
  ZoomInOutlined,
  ZoomOutOutlined,
  FullscreenOutlined
} from '@ant-design/icons';
import SimpleCodeEditor from '../../components/SimpleCodeEditor';
import { getStudentExerciseDetail, submitAnswer, completeExercise } from '../../api/exercises';
import { debounce, throttle } from 'lodash';

const { Header, Sider, Content } = Layout;
const { Title, Text, Paragraph } = Typography;
const { Group } = Radio;
const { Link } = Anchor;

// 添加图片查看器组件
const ImageViewer = ({ src, alt }) => {
  const [visible, setVisible] = useState(false);
  const [scale, setScale] = useState(1);
  const imgRef = useRef(null);

  const handleWheel = useCallback(
    throttle((e) => {
      if (!visible) return;
      e.preventDefault();
      
      const delta = e.deltaY > 0 ? -0.1 : 0.1;
      setScale(prevScale => {
        const newScale = prevScale + delta;
        return Math.max(0.2, Math.min(3, newScale)); // 限制缩放范围在0.2-3倍之间
      });
    }, 50), // 添加50ms的节流，减少连续触发
  [visible]);

  useEffect(() => {
    const currentImg = imgRef.current;
    if (currentImg) {
      currentImg.addEventListener('wheel', handleWheel);
      return () => {
        currentImg.removeEventListener('wheel', handleWheel);
      };
    }
  }, [handleWheel]);

  return (
    <>
      <div 
        className="question-image" 
        style={{ 
          display: 'inline-block',
          margin: '10px 0',
          cursor: 'zoom-in',
          maxWidth: '100%',
          maxHeight: '250px',
          position: 'relative'
        }}
        onClick={() => setVisible(true)}
      >
        <img 
          src={src} 
          alt={alt || "题目图片"} 
          style={{ 
            maxWidth: '100%', 
            maxHeight: '250px',
            border: '1px solid #f0f0f0',
            borderRadius: '4px'
          }} 
        />
        <div style={{ 
          position: 'absolute', 
          right: '8px', 
          top: '8px', 
          backgroundColor: 'rgba(255,255,255,0.8)', 
          padding: '2px 6px', 
          borderRadius: '4px' 
        }}>
          <FullscreenOutlined />
        </div>
      </div>
      
      <Modal
        open={visible}
        footer={null}
        onCancel={() => {
          setVisible(false);
          setScale(1);
        }}
        width="80%"
        centered
        closable
        destroyOnClose
        title={
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span>图片查看</span>
            <Space>
              <Button 
                icon={<ZoomOutOutlined />} 
                onClick={() => setScale(s => Math.max(0.2, s - 0.1))}
              />
              <span>{Math.round(scale * 100)}%</span>
              <Button 
                icon={<ZoomInOutlined />} 
                onClick={() => setScale(s => Math.min(3, s + 0.1))}
              />
              <Button 
                onClick={() => setScale(1)}
              >
                重置
              </Button>
            </Space>
          </div>
        }
      >
        <div 
          style={{ 
            display: 'flex', 
            justifyContent: 'center', 
            overflow: 'auto', 
            maxHeight: 'calc(80vh - 100px)' 
          }}
        >
          <img 
            ref={imgRef}
            src={src} 
            alt={alt || "题目图片"} 
            style={{ 
              transform: `scale(${scale})`,
              transformOrigin: 'center center',
              transition: 'transform 0.1s',
              maxWidth: '100%'
            }} 
          />
        </div>
        <div style={{ textAlign: 'center', marginTop: '10px', color: '#888' }}>
          使用鼠标滚轮缩放图片，或使用上方控制按钮
        </div>
      </Modal>
    </>
  );
};

// 注意：移除了 suppressResizeObserverErrors 函数

const ExamPage = () => {
  const { assignmentId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [exercise, setExercise] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [answers, setAnswers] = useState({});
  const [timeLeft, setTimeLeft] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [confirmSubmit, setConfirmSubmit] = useState(false);
  const [completed, setCompleted] = useState(false);
  const [currentQuestionId, setCurrentQuestionId] = useState(null);
  
  // 题目分组
  const [questionsByType, setQuestionsByType] = useState({});
  
  // 创建题目引用对象，用于滚动定位
  const questionRefs = useRef({});
  const sectionRefs = useRef({});

  // 页面加载时获取练习详情
  useEffect(() => {
    const fetchExerciseDetails = async () => {
      try {
        setLoading(true);
        const data = await getStudentExerciseDetail(assignmentId);
        setExercise(data);
        
        // 处理题目并按类型分组
        const questions = data.questions || [];
        setQuestions(questions);
        
        // 将题目按类型分组
        const groupedQuestions = {};
        questions.forEach(q => {
          // 标准化类型名称，确保一致性
          let typeKey = normalizeTypeName(q.type_name);
          
          if (!groupedQuestions[typeKey]) {
            groupedQuestions[typeKey] = [];
          }
          groupedQuestions[typeKey].push(q);
        });
        
        setQuestionsByType(groupedQuestions);
        
        // 初始化答案对象
        const initialAnswers = {};
        questions.forEach(q => {
          let typeKey = normalizeTypeName(q.type_name);
          
          if (typeKey === 'multiple_choice') {
            initialAnswers[q.id] = [];
          } else if (typeKey === 'single_choice' || typeKey === 'true_false') {
            initialAnswers[q.id] = '';
          } else if (typeKey === 'coding') {
            initialAnswers[q.id] = ''; // 编程题初始化为空
          }
        });
        setAnswers(initialAnswers);
        
        // 设置当前题目为第一题
        if (questions && questions.length > 0) {
          setCurrentQuestionId(questions[0].id);
        }
        
        // 设置倒计时（如果有时间限制）
        if (data.duration) {
          setTimeLeft(data.duration * 60); // 转换为秒
        }
        
        setLoading(false);
      } catch (error) {
        console.error('获取练习详情失败:', error);
        message.error('获取练习详情失败，请稍后重试');
        setLoading(false);
      }
    };
    
    fetchExerciseDetails();
  }, [assignmentId]);

  // 标准化题型名称，确保一致性
  const normalizeTypeName = (type) => {
    if (!type) return 'unknown';
    
    type = type.toLowerCase();
    
    if (type.includes('single') || type === '单选题') {
      return 'single_choice';
    } else if (type.includes('multiple') || type === '多选题') {
      return 'multiple_choice';
    } else if (type.includes('true') || type.includes('false') || type === '判断题') {
      return 'true_false';
    } else if (type.includes('coding') || type.includes('programming') || type === '编程题') {
      return 'coding';
    }
    
    return type;
  };

  // 倒计时
  useEffect(() => {
    if (loading || !exercise || timeLeft <= 0 || completed) return;
    
    const timer = setInterval(() => {
      setTimeLeft(prev => {
        if (prev <= 1) {
          clearInterval(timer);
          handleSubmitExam();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    
    return () => clearInterval(timer);
  }, [loading, exercise, timeLeft, completed]);

  // 格式化时间显示
  const formatTime = (seconds) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  // 处理答案变更
  const handleAnswerChange = (questionId, value) => {
    setAnswers(prev => ({
      ...prev,
      [questionId]: value
    }));
  };

  // 处理多选题选项变化
  const handleMultipleChoiceChange = (questionId, optionValue, checked) => {
    setAnswers(prev => {
      const currentAnswers = [...(prev[questionId] || [])];
      
      if (checked && !currentAnswers.includes(optionValue)) {
        currentAnswers.push(optionValue);
      } else if (!checked && currentAnswers.includes(optionValue)) {
        const index = currentAnswers.indexOf(optionValue);
        currentAnswers.splice(index, 1);
      }
      
      return { ...prev, [questionId]: currentAnswers };
    });
  };

  // 处理代码变更
  const handleCodeChange = (questionId, code) => {
    setAnswers(prev => ({
      ...prev,
      [questionId]: code
    }));
  };

  // 滚动到指定题目
  const scrollToQuestion = (questionId) => {
    setCurrentQuestionId(questionId);
    if (questionRefs.current[questionId]) {
      questionRefs.current[questionId].scrollIntoView({ 
        behavior: 'smooth', 
        block: 'start' 
      });
    }
  };
  
  // 滚动到指定题型部分
  const scrollToSection = (sectionType) => {
    if (sectionRefs.current[sectionType]) {
      sectionRefs.current[sectionType].scrollIntoView({ 
        behavior: 'smooth', 
        block: 'start' 
      });
    }
  };

  // 保存当前答案
  const handleSave = async () => {
    try {
      // 这里可以实现保存当前答案的逻辑
      message.success('已保存当前进度');
    } catch (error) {
      console.error('保存失败:', error);
      message.error('保存失败，请稍后重试');
    }
  };

  // 提交考试
  const handleSubmitExam = async () => {
    try {
      setSubmitting(true);
      
      // 构建提交数据
      const submissionData = {
        assignment_id: assignmentId,
        answers: Object.entries(answers).map(([questionId, answer]) => ({
          question_id: questionId,
          answer: typeof answer === 'object' ? JSON.stringify(answer) : answer
        }))
      };
      
      // 调用API提交考试
      await completeExercise(assignmentId);
      
      message.success('试卷已成功提交！');
      setCompleted(true);
      setConfirmSubmit(false);
      
      // 延迟2秒后返回练习列表
      setTimeout(() => {
        navigate('/student/exercises');
      }, 2000);
    } catch (error) {
      console.error('提交失败:', error);
      message.error('提交失败，请稍后重试');
    } finally {
      setSubmitting(false);
    }
  };

  // 计算答题进度
  const calculateProgress = () => {
    if (!questions.length) return 0;
    
    const answeredCount = Object.values(answers).filter(ans => {
      if (Array.isArray(ans)) return ans.length > 0;
      return ans && ans.trim() !== '';
    }).length;
    
    return Math.floor((answeredCount / questions.length) * 100);
  };

  // 获取题型图标
  const getQuestionTypeIcon = (type) => {
    const typeKey = normalizeTypeName(type);
    
    switch(typeKey) {
      case 'single_choice':
        return <QuestionCircleOutlined />;
      case 'multiple_choice':
        return <CheckSquareOutlined />;
      case 'true_false':
        return <CheckCircleOutlined />;
      case 'coding':
        return <CodeOutlined />;
      default:
        return <QuestionCircleOutlined />;
    }
  };

  // 获取题型显示文本
  const getQuestionTypeText = (type) => {
    const typeKey = normalizeTypeName(type);
    
    const typeMap = {
      'single_choice': '单选题',
      'multiple_choice': '多选题',
      'true_false': '判断题',
      'coding': '编程题'
    };
    
    return typeMap[typeKey] || '未知类型';
  };
  
  // 获取题型显示序号
  const getTypeIndex = (type) => {
    const typeKey = normalizeTypeName(type);
    
    const typeOrder = {
      'single_choice': 1,
      'true_false': 2,
      'multiple_choice': 3,
      'coding': 4
    };
    
    return typeOrder[typeKey] || 99;
  };
  
  // 获取排序后的题型数组
  const getSortedTypes = () => {
    return Object.keys(questionsByType).sort((a, b) => getTypeIndex(a) - getTypeIndex(b));
  };
  
  // 获取题型题号
  const getQuestionNumber = (question) => {
    const typeKey = normalizeTypeName(question.type_name);
    const typeQuestions = questionsByType[typeKey] || [];
    const index = typeQuestions.findIndex(q => q.id === question.id);
    return index + 1;
  };

  // 自定义处理HTML的函数，替换img标签为自定义组件的渲染方式
  const processHtml = (html) => {
    // 创建一个临时的DOM元素来解析HTML
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = html;
    
    // 处理所有img标签
    const imgElements = tempDiv.querySelectorAll('img');
    imgElements.forEach(img => {
      const imgWrapper = document.createElement('span');
      imgWrapper.className = 'custom-image-wrapper';
      imgWrapper.setAttribute('data-src', img.src);
      imgWrapper.setAttribute('data-alt', img.alt || '');
      img.parentNode.replaceChild(imgWrapper, img);
    });
    
    return tempDiv.innerHTML;
  };

  // 在题目内容中查找并渲染自定义图片查看器
  const renderProcessedHtml = (html) => {
    const processedHtml = processHtml(html);
    
    // 使用dangerouslySetInnerHTML渲染处理后的HTML
    return (
      <div
        dangerouslySetInnerHTML={{ __html: processedHtml }}
        ref={contentRef}
        style={{ fontSize: 16, marginBottom: '16px' }}
        className="question-content"
      />
    );
  };

  // 在组件挂载后，将特定的DOM元素替换为React组件
  const contentRef = useRef(null);
  
  useEffect(() => {
    if (!contentRef.current) return;
    
    // 查找所有自定义标记的图片包装器
    const imgWrappers = contentRef.current.querySelectorAll('.custom-image-wrapper');
    
    imgWrappers.forEach(wrapper => {
      const src = wrapper.getAttribute('data-src');
      const alt = wrapper.getAttribute('data-alt');
      
      // 创建用于渲染ImageViewer组件的容器
      const container = document.createElement('div');
      wrapper.parentNode.replaceChild(container, wrapper);
      
      // 使用ReactDOM渲染组件
      // 注意：在生产中我们通常会使用React Portal或其他方式，这里简化处理
      // ReactDOM.render(<ImageViewer src={src} alt={alt} />, container);
      
      // 由于不能直接使用ReactDOM.render，我们通过设置属性来模拟组件
      container.className = 'image-viewer-container';
      container.innerHTML = `
        <div style="display: inline-block; cursor: zoom-in; margin: 10px 0;">
          <img 
            src="${src}" 
            alt="${alt}" 
            style="max-width: 100%; max-height: 250px; border: 1px solid #f0f0f0; border-radius: 4px;"
            onclick="window.open('${src}', '_blank')"
          />
          <div style="position: absolute; right: 8px; top: 8px; background-color: rgba(255,255,255,0.8); padding: 2px 6px; border-radius: 4px;">
            <span style="font-size: 14px;">🔍</span>
          </div>
        </div>
      `;
    });
  }, [questions, currentQuestionId]);

  // 修改renderQuestion函数中渲染题目内容的部分
  const renderQuestion = (question, globalIndex) => {
    const typeKey = normalizeTypeName(question.type_name);
    const questionNumber = getQuestionNumber(question);
    
    return (
      <div 
        key={question.id} 
        ref={el => questionRefs.current[question.id] = el}
        style={{ 
          marginBottom: '40px', 
          padding: '20px',
          borderRadius: '8px',
          backgroundColor: currentQuestionId === question.id ? '#f0f8ff' : 'white',
          border: currentQuestionId === question.id ? '1px solid #1890ff' : '1px solid #f0f0f0'
        }}
        id={`question-${question.id}`}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
          <Title level={4} style={{ margin: 0 }}>
            {questionNumber}. 
            <Tag color="blue" style={{ marginLeft: 8, marginRight: 8 }}>
              {getQuestionTypeText(question.type_name)}
            </Tag>
            ({question.points || 10}分)
          </Title>
          
          {question.audio_url && (
            <Button 
              type="text" 
              icon={<SoundOutlined />} 
              onClick={() => {
                const audio = new Audio(question.audio_url);
                audio.play();
              }}
            >
              播放音频
            </Button>
          )}
        </div>

        {/* 题目内容 */}
        <div 
          style={{ fontSize: 16, marginBottom: '16px' }}
          dangerouslySetInnerHTML={{ __html: question.question_text }}
          className="question-content"
        />
        
        {/* 如果是编程题，显示输入和输出样例 */}
        {normalizeTypeName(question.type_name) === 'coding' && (
          <div style={{ marginBottom: '16px' }}>
            {question.sample_input && (
              <div style={{ marginBottom: '12px' }}>
                <div style={{ 
                  fontWeight: 'bold', 
                  marginBottom: '8px',
                  backgroundColor: '#e6f7ff',
                  padding: '4px 12px',
                  borderLeft: '4px solid #1890ff',
                  borderRadius: '2px'
                }}>
                  示例输入：
                </div>
                <pre style={{
                  backgroundColor: '#f5f5f5',
                  padding: '12px',
                  borderRadius: '4px',
                  overflowX: 'auto',
                  fontFamily: 'Consolas, "Courier New", monospace',
                  fontSize: '14px',
                  lineHeight: '1.5',
                  border: '1px solid #e8e8e8'
                }}>
                  {question.sample_input}
                </pre>
              </div>
            )}
            
            {question.sample_output && (
              <div>
                <div style={{ 
                  fontWeight: 'bold', 
                  marginBottom: '8px',
                  backgroundColor: '#f6ffed',
                  padding: '4px 12px',
                  borderLeft: '4px solid #52c41a',
                  borderRadius: '2px'
                }}>
                  示例输出：
                </div>
                <pre style={{
                  backgroundColor: '#f5f5f5',
                  padding: '12px',
                  borderRadius: '4px',
                  overflowX: 'auto',
                  fontFamily: 'Consolas, "Courier New", monospace',
                  fontSize: '14px',
                  lineHeight: '1.5',
                  border: '1px solid #e8e8e8'
                }}>
                  {question.sample_output}
                </pre>
              </div>
            )}
          </div>
        )}
        
        {/* 若题目中包含图片URL但不是HTML标签，显示图片 */}
        {question.image_url && (
          <ImageViewer src={question.image_url} alt="题目图片" />
        )}

        {typeKey === 'single_choice' && (
          <Group 
            onChange={e => handleAnswerChange(question.id, e.target.value)}
            value={answers[question.id]}
          >
            <Space direction="vertical" style={{ width: '100%' }}>
              {(() => {
                try {
                  const options = JSON.parse(question.options || '[]');
                  return Array.isArray(options) ? 
                    options.map((option, idx) => (
                      <Radio key={idx} value={option.value || option.id}>
                        <span dangerouslySetInnerHTML={{ __html: option.label || option.text }} />
                      </Radio>
                    )) :
                    Object.entries(options).map(([key, value], idx) => (
                      <Radio key={idx} value={key}>
                        <span dangerouslySetInnerHTML={{ __html: `${key}. ${value}` }} />
                      </Radio>
                    ));
                } catch (e) {
                  console.error('解析选项失败:', e);
                  return <div>选项格式错误</div>;
                }
              })()}
            </Space>
          </Group>
        )}
        
        {typeKey === 'multiple_choice' && (
          <div>
            {(() => {
              try {
                const options = JSON.parse(question.options || '[]');
                return Array.isArray(options) ? 
                  options.map((option, idx) => (
                    <div key={idx} style={{ marginBottom: 12 }}>
                      <Checkbox
                        checked={(answers[question.id] || []).includes(option.value || option.id)}
                        onChange={e => handleMultipleChoiceChange(
                          question.id,
                          option.value || option.id,
                          e.target.checked
                        )}
                      >
                        <span dangerouslySetInnerHTML={{ __html: option.label || option.text }} />
                      </Checkbox>
                    </div>
                  )) :
                  Object.entries(options).map(([key, value], idx) => (
                    <div key={idx} style={{ marginBottom: 12 }}>
                      <Checkbox
                        checked={(answers[question.id] || []).includes(key)}
                        onChange={e => handleMultipleChoiceChange(
                          question.id,
                          key,
                          e.target.checked
                        )}
                      >
                        <span dangerouslySetInnerHTML={{ __html: `${key}. ${value}` }} />
                      </Checkbox>
                    </div>
                  ));
              } catch (e) {
                console.error('解析选项失败:', e);
                return <div>选项格式错误</div>;
              }
            })()}
          </div>
        )}
        
        {typeKey === 'true_false' && (
          <Group 
            onChange={e => handleAnswerChange(question.id, e.target.value)}
            value={answers[question.id]}
          >
            <Space direction="vertical" style={{ width: '100%' }}>
              <Radio value="true">正确</Radio>
              <Radio value="false">错误</Radio>
            </Space>
          </Group>
        )}
        
        {typeKey === 'coding' && (
          <div style={{ height: '400px', marginTop: '16px' }}>
            <SimpleCodeEditor
              initialCode={answers[question.id] || '// 在此编写代码'}
              language={question.language || 'javascript'}
              height="100%"
              onSave={throttle(code => handleCodeChange(question.id, code), 300)}
              onRun={throttle(code => handleCodeChange(question.id, code), 300)}
            />
          </div>
        )}
      </div>
    );
  };

  // 注意：移除了 ResizeObserver 错误抑制的 useEffect

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <Spin size="large" tip="加载试卷中..." />
      </div>
    );
  }

  if (!exercise) {
    return (
      <div style={{ padding: '50px', textAlign: 'center' }}>
        <Title level={4}>获取练习失败</Title>
        <Button type="primary" onClick={() => navigate('/student/exercises')}>
          返回练习列表
        </Button>
      </div>
    );
  }

  // 按题型排序
  const sortedTypes = getSortedTypes();

  return (
    <Layout style={{ minHeight: '100vh' }}>
      <Header style={{ 
        background: '#fff', 
        padding: '0 24px', 
        display: 'flex', 
        justifyContent: 'space-between',
        alignItems: 'center',
        boxShadow: '0 1px 4px rgba(0, 0, 0, 0.1)',
        position: 'sticky',
        top: 0,
        zIndex: 10
      }}>
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <Title level={4} style={{ margin: 0 }}>
            {exercise.title}
          </Title>
          <Tag color="blue" style={{ marginLeft: 16 }}>
            备战素养大赛
          </Tag>
        </div>
        
        <div>
          <Space>
            <Tooltip title="倒计时">
              <Tag 
                color={timeLeft < 300 ? 'red' : timeLeft < 600 ? 'orange' : 'blue'} 
                style={{ marginRight: 8 }}
              >
                <ClockCircleOutlined /> {formatTime(timeLeft)}
              </Tag>
            </Tooltip>
            
            <Button 
              icon={<SaveOutlined />} 
              onClick={handleSave}
              style={{ marginRight: 8 }}
            >
              保存
            </Button>
            
            <Button 
              type="primary" 
              icon={<CheckOutlined />} 
              onClick={() => setConfirmSubmit(true)}
              loading={submitting}
            >
              提交试卷
            </Button>
          </Space>
        </div>
      </Header>
      
      <Layout>
        <Affix offsetTop={64}>
          <Sider 
            width={220} 
            theme="light"
            style={{ 
              height: 'calc(100vh - 64px)', 
              overflow: 'auto',
              boxShadow: '2px 0 4px rgba(0, 0, 0, 0.1)'
            }}
          >
            <div 
              style={{ 
                padding: '16px', 
                textAlign: 'center',
                borderBottom: '1px solid #f0f0f0' 
              }}
            >
              <Progress 
                percent={calculateProgress()} 
                size="small" 
                status="active"
                style={{ marginBottom: 8 }}
              />
              <Text>已完成 {calculateProgress()}%</Text>
            </div>
            
            <Menu
              mode="inline"
              selectedKeys={[currentQuestionId ? currentQuestionId.toString() : '']}
              style={{ borderRight: 0 }}
            >
              {sortedTypes.map((type, typeIndex) => {
                const typeName = getQuestionTypeText(type);
                const questions = questionsByType[type] || [];
                
                return (
                  <Menu.SubMenu 
                    key={`type-${type}`} 
                    icon={getQuestionTypeIcon(type)}
                    title={`${String.fromCharCode(65 + typeIndex)}、${typeName}`}
                    onTitleClick={() => scrollToSection(type)}
                  >
                    {questions.map((q, qIndex) => (
                      <Menu.Item 
                        key={q.id.toString()}
                        onClick={() => scrollToQuestion(q.id)}
                      >
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                          <span>第 {qIndex + 1} 题</span>
                          <Badge 
                            status={answers[q.id] ? 'success' : 'default'} 
                          />
                        </div>
                      </Menu.Item>
                    ))}
                  </Menu.SubMenu>
                );
              })}
            </Menu>
          </Sider>
        </Affix>
        
        <Content style={{ padding: '24px', background: '#fff', overflow: 'auto' }}>
          <Card title="试卷内容" bordered={false} style={{ marginBottom: 16 }}>
            <Paragraph>
              <strong>试卷说明：</strong> {exercise.description || '请认真答题，按照要求完成所有题目。'}
            </Paragraph>
            <Paragraph>
              <strong>总分：</strong> {exercise.total_points} 分
              <Divider type="vertical" />
              <strong>及格分：</strong> {exercise.passing_points} 分
              <Divider type="vertical" />
              <strong>题目数：</strong> {questions.length} 道
            </Paragraph>
          </Card>
          
          <div style={{ marginBottom: 16 }}>
            {sortedTypes.map((type, typeIndex) => {
              const typeName = getQuestionTypeText(type);
              const typeQuestions = questionsByType[type] || [];
              
              return (
                <div key={`section-${type}`} ref={el => sectionRefs.current[type] = el}>
                  <Title 
                    level={3} 
                    id={`section-${type}`}
                    style={{ marginTop: 30, marginBottom: 20 }}
                  >
                    {String.fromCharCode(65 + typeIndex)}、{typeName}（每题{typeQuestions[0]?.points || 10}分）
                  </Title>
                  
                  {typeQuestions.map((question, qIndex) => 
                    renderQuestion(question, qIndex)
                  )}
                </div>
              );
            })}
          </div>
          
          <div style={{ display: 'flex', justifyContent: 'center', padding: '30px 0' }}>
            <Button 
              type="primary" 
              size="large"
              onClick={() => setConfirmSubmit(true)}
              loading={submitting}
            >
              提交试卷
            </Button>
          </div>
        </Content>
      </Layout>
      
      {/* 提交确认对话框 */}
      <Modal
        title="确认提交"
        open={confirmSubmit}
        onCancel={() => setConfirmSubmit(false)}
        onOk={handleSubmitExam}
        okText="确认提交"
        cancelText="继续做题"
        confirmLoading={submitting}
      >
        <p>确定要提交试卷吗？提交后将无法再修改答案。</p>
        <p>已完成 {calculateProgress()}% 的题目。</p>
        {calculateProgress() < 100 && (
          <p style={{ color: 'orange' }}>
            注意：还有 {questions.length - Object.keys(answers).filter(k => answers[k] && (Array.isArray(answers[k]) ? answers[k].length > 0 : answers[k].trim() !== '')).length} 题未完成!
          </p>
        )}
      </Modal>
    </Layout>
  );
};

export default ExamPage; 